// Isometric printed-part visualization (SVG) — the design bundle's "tech sample" mockup.
// Robust (no CSS-3D layer collapse), reacts to material / heatmap / cross-section / layer
// reveal. Look only; the verdict comes from the INSPECTOR HUD.

export function IsoPart({
  color,
  heatmap,
  reveal,
  section,
  size = 360,
  shadow = true,
  viewBox = "-170 -210 340 340",
}: {
  color: string;
  heatmap: boolean;
  reveal: number;
  section: boolean;
  size?: number;
  shadow?: boolean;
  viewBox?: string;
}) {
  const s = 52;
  const w = s;
  const h = s * 0.5; // 2:1 isometric unit
  const bx = 2.7;
  const by = 2.1;
  const bzFull = 2.0;
  const bz = bzFull * Math.max(0.04, reveal);
  const xMax = section ? bx * 0.52 : bx; // cross-section trims the +x half
  const cx = 0;
  const cy = 0;
  const P = (px: number, py: number, pz: number) =>
    `${(cx + (px - py) * w).toFixed(1)},${(cy + (px + py) * h - pz * s).toFixed(1)}`;

  const top = [P(0, 0, bz), P(xMax, 0, bz), P(xMax, by, bz), P(0, by, bz)].join(" ");
  const right = [P(xMax, 0, bz), P(xMax, by, bz), P(xMax, by, 0), P(xMax, 0, 0)].join(" ");
  const front = [P(0, by, bz), P(xMax, by, bz), P(xMax, by, 0), P(0, by, 0)].join(" ");

  const cTop = heatmap ? "#ffc24d" : `color-mix(in oklab, ${color} 88%, white)`;
  const cRight = heatmap ? "#ff7a2c" : color;
  const cFront = heatmap ? "#ff5236" : `color-mix(in oklab, ${color} 64%, black)`;
  const cCut = "#0c1320";

  const holeCx = cx + (bx * 0.62 - by * 0.5) * w;
  const holeCy = cy + (bx * 0.62 + by * 0.5) * h - bz * s;

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      style={{ overflow: "visible", filter: shadow ? "drop-shadow(0 26px 30px rgba(0,0,0,0.5))" : "none" }}
    >
      <defs>
        <pattern id="infill" width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="9" stroke={color} strokeOpacity="0.5" strokeWidth="2.4" />
        </pattern>
      </defs>
      <ellipse cx={cx} cy={cy + (bx + by) * h * 0.5} rx={(bx + by) * w * 0.5} ry={(bx + by) * h * 0.5} fill="#000" opacity="0.28" />
      <polygon points={front} fill={cFront} stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
      <polygon points={right} fill={section ? cCut : cRight} stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
      {section && <polygon points={right} fill="url(#infill)" />}
      <polygon points={top} fill={cTop} stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
      {!section && <ellipse cx={holeCx} cy={holeCy} rx={w * 0.42} ry={h * 0.42} fill="#0a0e16" opacity="0.85" />}
      <polygon points={top} fill="none" stroke="var(--accent-3)" strokeOpacity={reveal < 1 ? 0.9 : 0} strokeWidth="2" />
    </svg>
  );
}
